import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stack,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Alert,
  Grid,
} from '@mui/material';
import {
  Store as StoreIcon,
  Dashboard as BulkIcon,
  Schedule as HoursIcon,
  DeviceHub as ChannelsIcon,
  Inventory as ItemsIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';

export const StoreManagementDemo: React.FC = () => {
  const [viewMode, setViewMode] = useState<'store' | 'bulk'>('store');
  const [activeStep, setActiveStep] = useState(0);

  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'store' | 'bulk' | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setActiveStep(0);
    }
  };

  const storeViewSteps = [
    {
      label: 'Select Store(s)',
      description: 'Choose one or more stores from the list',
      content: (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Stack spacing={1}>
            <Typography variant="body2">✓ Store 099010 - Ashford DT</Typography>
            <Typography variant="body2">✓ Store 099004 - Broadstairs</Typography>
            <Typography variant="body2" color="text.secondary">
              Store 099002 - Ipswich
            </Typography>
          </Stack>
        </Box>
      ),
    },
    {
      label: 'Choose Function',
      description: 'Select what you want to manage for these stores',
      content: (
        <Stack direction="row" spacing={1}>
          <Chip icon={<HoursIcon />} label="Store Hours" color="primary" />
          <Chip icon={<ChannelsIcon />} label="Channels" variant="outlined" />
          <Chip icon={<ItemsIcon />} label="Items" variant="outlined" />
        </Stack>
      ),
    },
    {
      label: 'Manage Settings',
      description: 'View and edit all settings for selected stores',
      content: (
        <Alert severity="success" variant="outlined">
          You can now see and manage Store Hours for both selected stores side-by-side
        </Alert>
      ),
    },
  ];

  const bulkViewSteps = [
    {
      label: 'Choose Function',
      description: 'Select what type of changes you want to make',
      content: (
        <Stack direction="row" spacing={1}>
          <Chip icon={<HoursIcon />} label="Store Hours" color="primary" />
          <Chip icon={<ChannelsIcon />} label="Channels" variant="outlined" />
          <Chip icon={<ItemsIcon />} label="Items" variant="outlined" />
        </Stack>
      ),
    },
    {
      label: 'Configure Changes',
      description: 'Set up the changes you want to apply',
      content: (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            Example: Set all stores to close at 10 PM on weekdays
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Monday-Friday: 8:00 AM - 10:00 PM
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Select Target Stores',
      description: 'Choose which stores to apply these changes to',
      content: (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            Apply to: 25 stores selected
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can filter by region, brand, or select individually
          </Typography>
        </Box>
      ),
    },
    {
      label: 'Review & Apply',
      description: 'Preview changes and apply to all selected stores',
      content: (
        <Alert severity="info" variant="outlined">
          25 stores will be updated with new weekday hours
        </Alert>
      ),
    },
  ];

  const currentSteps = viewMode === 'store' ? storeViewSteps : bulkViewSteps;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Store Management UX Flow
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Our hybrid approach offers two ways to manage your stores, optimized for different use
        cases:
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StoreIcon color="primary" />
                  <Typography variant="h6">Store View Mode</Typography>
                </Box>
                <Typography variant="body2">
                  <strong>Best for:</strong> Managing multiple settings for specific stores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Managing a single store comprehensively
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Comparing settings across a few stores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Making different changes to different stores
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BulkIcon color="secondary" />
                  <Typography variant="h6">Bulk Operations Mode</Typography>
                </Box>
                <Typography variant="body2">
                  <strong>Best for:</strong> Applying the same changes to many stores
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Updating hours for all stores in a region
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Enabling/disabling channels across multiple locations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Applying templates or presets to store groups
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="large"
            >
              <ToggleButton value="store" aria-label="store view">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2 }}>
                  <StoreIcon />
                  <Typography>Store View</Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="bulk" aria-label="bulk operations">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2 }}>
                  <BulkIcon />
                  <Typography>Bulk Operations</Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          <Typography variant="h6">
            {viewMode === 'store' ? 'Store-First Workflow' : 'Function-First Workflow'}
          </Typography>

          <Stepper activeStep={activeStep} orientation="vertical">
            {currentSteps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="subtitle1">{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Stack spacing={2}>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                    {step.content}
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => setActiveStep(index + 1)}
                        sx={{ mt: 1, mr: 1 }}
                        disabled={index === currentSteps.length - 1}
                        endIcon={<ArrowIcon />}
                      >
                        {index === currentSteps.length - 1 ? 'Complete' : 'Continue'}
                      </Button>
                      <Button
                        disabled={index === 0}
                        onClick={() => setActiveStep(index - 1)}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        Back
                      </Button>
                    </Box>
                  </Stack>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === currentSteps.length && (
            <Paper elevation={0} sx={{ p: 3, bgcolor: 'success.light' }}>
              <Typography variant="h6" gutterBottom>
                Workflow Complete! 🎉
              </Typography>
              <Typography paragraph>
                {viewMode === 'store'
                  ? 'You have successfully managed settings for your selected stores.'
                  : 'You have successfully applied bulk changes to multiple stores.'}
              </Typography>
              <Button onClick={() => setActiveStep(0)}>Reset</Button>
            </Paper>
          )}
        </Stack>
      </Paper>

      <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="h6" gutterBottom>
          Key Features Across Both Modes:
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              🔍 Smart Filtering
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search and filter stores by name, location, brand, or status
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              💾 Templates & Presets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Save common configurations and apply them quickly
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom>
              ↩️ Undo/Redo Support
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Safely make changes with full undo/redo capability
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
